import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { Clock, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { ARTICLE_IMAGES } from '../data/articleImages';
import { FlowBackground } from '../components/home/FlowBackground';
import { PAGE_BG, GLASS, PAGE_TITLE } from '../components/home/glass';

export default function Blog() {
    const { t, lang } = useLanguage();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState('');

    useEffect(() => {
        axios.get(`${API}/blog/articles`)
            .then(res => { setArticles(res.data); setLoading(false); })
            .catch(() => setLoading(false));
    }, []);

    const getTitle = (a) => lang === 'nl' ? a.title_nl : a.title_en;
    const getExcerpt = (a) => lang === 'nl' ? a.excerpt_nl : a.excerpt_en;

    // Three curated categories. Each article is matched on whether ANY of its tags
    // overlaps this list. Add tags here as new articles are published.
    const CATEGORIES = [
        {
            key: 'stress',
            label: lang === 'nl' ? 'Stress & burn-out' : 'Stress & burnout',
            tags: ['Stress', 'Cortisol', 'HPA Axis', 'Burnout', 'Rumination',
                   'Cognitive Hyperarousal', 'Amygdala', 'Emotional Exhaustion',
                   'Stress Recovery', 'Brain Changes'],
        },
        {
            key: 'science',
            label: lang === 'nl' ? 'Slaapwetenschap' : 'Sleep science',
            tags: ['Sleep Quality', 'Sleep Architecture', 'Deep Sleep',
                   'Slow-Wave Sleep', 'Circadian Rhythm', 'Sleep Fragmentation',
                   'REM Sleep', 'HRV', 'Light Exposure', 'Social Jetlag', 'Blue Light',
                   'Neuroplasticity', 'Growth Hormone'],
        },
        {
            key: 'falling-asleep',
            label: lang === 'nl' ? 'Sneller in slaap vallen' : 'Falling asleep faster',
            tags: ['Sleep Onset', 'Sleep Latency', 'Wind-Down Routine', 'CBT-I',
                   'Neuroscience', 'VLPO', 'GABA', 'Adenosine', 'Melatonin',
                   'Body Temperature', 'Sleep Science', 'Insomnia'],
        },
    ];

    const filtered = activeCat
        ? articles.filter(a => {
            const cat = CATEGORIES.find(c => c.key === activeCat);
            if (!cat) return true;
            return (a.tags || []).some(t => cat.tags.includes(t));
        })
        : articles;

    const featured = filtered[0];
    const rest = filtered.slice(1);

    const renderImage = (article, idx, cls) => (
        ARTICLE_IMAGES[article.slug] ? (
            <img
                src={ARTICLE_IMAGES[article.slug]}
                alt={getTitle(article)}
                loading="lazy"
                decoding="async"
                width="600"
                height="400"
                className={cls}
            />
        ) : (
            <div
                className="w-full h-full flex items-end p-5"
                style={{
                    background: idx % 2 === 0
                        ? 'linear-gradient(135deg, #0047AB15 0%, #0047AB08 100%)'
                        : 'linear-gradient(135deg, #FF69B415 0%, #FF69B408 100%)'
                }}
            >
                <div className="flex flex-wrap gap-1.5">
                    {article.tags?.slice(0, 2).map(tag => (
                        <span key={tag} className="bg-white/80 backdrop-blur-sm text-primary text-xs font-semibold px-2.5 py-1 rounded-full">
                            {tag}
                        </span>
                    ))}
                </div>
            </div>
        )
    );

    return (
        <div>
            <SEO
                title="Articles | Sleepstack"
                description="Long-form articles on sleep biology, ingredient research, and what the literature actually says, for curious readers who want sources, not slogans."
                path="/blog"
            />
            <PreSaleBanner />
            <Navbar />
            <main className="relative isolate glass-scope" style={{ paddingTop: 'var(--page-offset, 128px)', background: PAGE_BG }}>
                <FlowBackground variant="blog" />
                {/* Header */}
                <section className="section-padding bg-white">
                    <div className="section-container">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className={`max-w-3xl p-6 sm:p-8 lg:p-10 ${GLASS}`}
                        >
                            <h1 className={`${PAGE_TITLE} mb-4`} data-testid="blog-title">
                                {t('blog.title')}
                            </h1>
                            <p className="text-muted-foreground text-lg max-w-xl">
                                {t('blog.subtitle')}
                            </p>
                        </motion.div>
                    </div>
                </section>

                {/* Article grid */}
                <section className="pb-24 bg-accent">
                    <div className="section-container">
                        {loading ? (
                            <div className="flex justify-center py-20">
                                <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : (
                            <>
                                {/* Featured story — editorial lead */}
                                {featured && (
                                    <motion.article
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5 }}
                                        className="card-base overflow-hidden group mb-10"
                                        data-testid={`article-card-${featured.id}`}
                                    >
                                        <Link to={`/blog/${featured.slug}`} className="grid grid-cols-1 md:grid-cols-2" data-testid="featured-article">
                                            <div className="h-56 md:h-auto md:min-h-[340px] overflow-hidden">
                                                {renderImage(featured, 0, 'w-full h-full object-cover transition-transform duration-500 group-hover:scale-105')}
                                            </div>
                                            <div className="p-7 md:p-10 lg:p-12 flex flex-col justify-center">
                                                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-secondary-dark mb-4">
                                                    {lang === 'nl' ? 'Uitgelicht' : 'Featured'}
                                                </div>
                                                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground leading-tight mb-4 group-hover:text-primary transition-colors">
                                                    {getTitle(featured)}
                                                </h2>
                                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed line-clamp-3 mb-6">
                                                    {getExcerpt(featured)}
                                                </p>
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                                                        {t('blog.readMore')}
                                                        <ArrowRight size={14} />
                                                    </span>
                                                    <span className="flex items-center gap-3 text-xs text-muted-foreground">
                                                        <span>{new Date(featured.published_at).toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short' })}</span>
                                                        <span className="flex items-center gap-1">
                                                            <Clock size={11} />
                                                            {featured.read_time} min
                                                        </span>
                                                    </span>
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.article>
                                )}

                                {/* Secondary stories */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {rest.map((article, i) => (
                                        <motion.article
                                            key={article.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4, delay: Math.min(i, 6) * 0.07 }}
                                            className="card-base overflow-hidden group"
                                            data-testid={`article-card-${article.id}`}
                                        >
                                            <Link to={`/blog/${article.slug}`}>
                                                {/* Article image */}
                                                <div className="h-48 overflow-hidden">
                                                    {renderImage(article, i, 'w-full h-full object-cover transition-transform duration-500 group-hover:scale-105')}
                                                </div>
                                                <div className="p-6">
                                                    <h2 className="font-heading text-xl text-foreground mb-2 group-hover:text-primary transition-colors leading-tight">
                                                        {getTitle(article)}
                                                    </h2>
                                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4 leading-relaxed">
                                                        {getExcerpt(article)}
                                                    </p>
                                                    <div className="flex items-center justify-end gap-3 text-xs text-muted-foreground">
                                                        <span>{new Date(article.published_at).toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-GB', { day: 'numeric', month: 'short' })}</span>
                                                        <span className="flex items-center gap-1">
                                                            <Clock size={11} />
                                                            {article.read_time} min
                                                        </span>
                                                    </div>
                                                </div>
                                            </Link>
                                        </motion.article>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
